import { Badge, Button, Flex, Input, Popover, Select, Tabs, TabsProps, Tooltip } from 'antd';
import { FC, useMemo, useState } from 'react';
import { SavedFilters, savedFiltersKey } from '../../types';
import { CloseOutlined, InfoCircleFilled, SettingOutlined } from '@ant-design/icons';
import { useToast } from '../../../../hooks/useToast';
import { hasCurrentView } from '../../has-current-view';
import { getCurrentView } from '../../get-current-view';
import { removeCurrentView } from '../../remove-current-view';

type Props = {
  pageKey: string;
  onSave: (viewName: string) => void;
  onSetView: (viewId: string) => void;
  onViewRemoved: () => void;
};

export const FiltersPopover: FC<Props> = ({ pageKey, onSave, onSetView, onViewRemoved }) => {
  const { success } = useToast();
  const [refreshKey, setRefreshKey] = useState(Date.now());
  const [viewName, setViewName] = useState<string | null>(null);
  const [viewId, setViewId] = useState<string | null>(getCurrentView(pageKey));
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [hasFilterViewApplied, setHasFilterViewApplied] = useState(hasCurrentView(pageKey));

  const availableFilterViews = useMemo(() => {
    const savedFilters = localStorage.getItem(savedFiltersKey) || '{}';
    const parsedFilters: SavedFilters = JSON.parse(savedFilters);

    const pageFilters = Object.values((parsedFilters[pageKey] || {}).filterViews || {})

      .map(filter => ({
        value: filter.id,
        label: filter.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return pageFilters;
  }, [pageKey, refreshKey]);

  const handleSaveFiltersView = () => {
    onSave(viewName!);
    success('Filters saved successfully');
    setIsPopoverOpen(false);
    setViewName(null);
    setRefreshKey(Date.now());
  };

  const handleSetCurrentFilterView = () => {
    onSetView(viewId!);
    setIsPopoverOpen(false);
    setHasFilterViewApplied(true);
  };

  const handleRemoveAppliedFilterView = () => {
    removeCurrentView(pageKey);
    setIsPopoverOpen(false);
    onViewRemoved();
    setHasFilterViewApplied(false);
    setViewId(null);
    setViewName(null);
  };

  const items: TabsProps['items'] = [
    {
      key: 'change-view',
      label: 'Change View',
      children: (
        <Flex vertical gap={8}>
          <Select
            options={availableFilterViews}
            placeholder="Select a view"
            onChange={setViewId}
            value={viewId}
          />
          <Flex gap={8} justify="flex-end">
            <Button onClick={() => setIsPopoverOpen(false)}>Cancel</Button>
            <Button type="primary" onClick={handleSetCurrentFilterView} disabled={!viewId}>
              Apply
            </Button>
          </Flex>
        </Flex>
      ),
    },
    {
      key: 'save-view',
      label: 'Save View',
      children: (
        <Flex vertical gap={8}>
          <Input
            placeholder="View Name"
            value={viewName || ''}
            onChange={e => setViewName(e.target.value || null)}
          />
          <Flex gap={8} justify="flex-end">
            <Button onClick={() => setIsPopoverOpen(false)}>Cancel</Button>
            <Button type="primary" onClick={handleSaveFiltersView} disabled={!viewName}>
              Save
            </Button>
          </Flex>
        </Flex>
      ),
    },
  ];

  return (
    <Flex gap={8}>
      <Popover
        content={<Tabs items={items} />}
        trigger={['click']}
        styles={{ root: { width: '500px' } }}
        open={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
      >
        <Badge
          count={hasFilterViewApplied ? <InfoCircleFilled style={{ color: '#f00' }} /> : 0}
          offset={[-30, 2]}
        >
          <Button
            icon={<SettingOutlined />}
            type="primary"
            onClick={() => setIsPopoverOpen(true)}
          />
        </Badge>
      </Popover>

      {hasFilterViewApplied && (
        <Tooltip title="Remove applied filter view">
          <Button icon={<CloseOutlined />} type="default" onClick={handleRemoveAppliedFilterView} />
        </Tooltip>
      )}
    </Flex>
  );
};
